import React, { useCallback, useEffect, useMemo, useState } from 'react'
import '../parity-dev/cfo/CfoPageShell.css'
import './CfoExperiencesPage.css'
import { ServerApiClient } from '../../data/ServerApiClient'
import type { AccountingProviderDescriptor, SageBusinessCandidate, ServerProject, ServerSite, ServerSiteSummary, ServerSiteWorkbookSummary, XeroOrganizationCandidate } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'
import { CfoCoreFileMenu } from '../parity-dev/cfo/CfoCoreFileMenu'
import { QuickBooksConnectLinkDialog, QuickBooksConnectLinkPanel } from '../parity-dev/cfo/QuickBooksConnectLinkPanel'
import { buildCfoRoutePath, buildFirmCockpitPath } from '../parity-dev/cfo/cfoRoutes'
import { CfoBrandMark, navigateTo } from './CfoSiteShared'
import { AccountingProviderMark } from './CfoSignupPage'
import {
  cfoSelectorCanonicalPath,
  cfoWorkbookAwaitingQuickBooksAdmin,
  cfoSiteCanAddExperience,
  cfoSiteCanManageProjects,
  cfoSiteCanManageUsers,
  cfoSiteProjectWorkbooks,
  cfoSiteUsesProjects,
  type CfoProjectNode,
  type CfoSiteProjectSection,
  resolveCfoAdminToolsPath,
  selectCfoSiteProjectSections,
} from './cfoExperiences'

/* Duo-tone glyphs matching the CFO shell `.ic` convention (translucent fill +
   1.7px stroke, both currentColor). One per tier so the role of a row is
   legible from its icon alone, independent of the (often duplicated) name. */
const SiteGlyph = () => (
  <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
    <path className="duo-fill" d="M4 21V4.5A1.5 1.5 0 0 1 5.5 3h7A1.5 1.5 0 0 1 14 4.5V21Z" />
    <path className="duo-stroke" d="M4 21V4.5A1.5 1.5 0 0 1 5.5 3h7A1.5 1.5 0 0 1 14 4.5V21" />
    <path className="duo-stroke" d="M14 9h4.5A1.5 1.5 0 0 1 20 10.5V21" />
    <path className="duo-stroke" d="M3 21h18" />
    <path className="duo-stroke" d="M7 7.5h3.5M7 11h3.5M7 14.5h3.5" />
  </svg>
)
const ProjectGlyph = () => (
  <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
    <path className="duo-fill" d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2h7A1.5 1.5 0 0 1 19 9.5V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5Z" />
    <path className="duo-stroke" d="M3 8V6.5A1.5 1.5 0 0 1 4.5 5h4l2 2h7A1.5 1.5 0 0 1 19 8.5V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8Z" />
  </svg>
)
const WorkbookGlyph = () => (
  <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
    <rect className="duo-fill" x="4" y="3" width="16" height="18" rx="2.5" />
    <rect className="duo-stroke" x="4" y="3" width="16" height="18" rx="2.5" />
    <path className="duo-stroke" d="M8 14.5 11 11l2.2 2.2L16 9.5" />
    <circle className="duo-stroke" cx="16" cy="9.5" r="0.6" fill="currentColor" />
  </svg>
)

type LoadStatus = 'loading' | 'ready' | 'error'
type ConnectPhase = 'idle' | 'creating' | 'connecting'
type ConnectTargetMode = 'existing_site' | 'new_site'
/** Who authorizes QuickBooks: the signed-in user now, or the company's
 * QuickBooks admin later, through a standing link. */
type QuickBooksAuthorizer = 'me' | 'admin'
type LinkTarget = { workbookId: string; companyLabel: string; reconnect: boolean; siteSlug: string | null }
type AccountingProvider = 'quickbooks' | 'xero' | 'sage_intacct' | 'sage_accounting' | 'netsuite'
const PROVIDER_ORDER: AccountingProvider[] = ['quickbooks', 'xero', 'sage_intacct', 'sage_accounting', 'netsuite']
const PROVIDER_NAMES: Record<AccountingProvider, string> = {
  quickbooks: 'QuickBooks',
  xero: 'Xero',
  sage_intacct: 'Sage Intacct',
  sage_accounting: 'Sage Accounting',
  netsuite: 'NetSuite',
}
/** CFO Core workbooks are named "{Company} CFO Core" at creation. */
const companyLabelFromWorkbookName = (name: string): string =>
  String(name || '').replace(/\s+CFO Core\s*$/i, '').trim() || String(name || '').trim()

type MoveWorkbookState = {
  workbook: ServerSiteWorkbookSummary
  sourceSite: ServerSite
  targetSiteId: string
  targetProjectId: string
  saving: boolean
  error: string | null
}

// The bare `.cfo-shell` root is a fixed 3-column app grid. Neutralize it so this
// top-level selector scrolls as a normal contained page while keeping the CFO
// canvas, tokens, and typography.
const shellStyle: React.CSSProperties = {
  display: 'block',
  height: '100%',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
}

export function CfoExperiencesPage({ api: apiProp }: { api?: ServerApiClient } = {}) {
  const api = useMemo(() => apiProp ?? new ServerApiClient(), [apiProp])

  const [status, setStatus] = useState<LoadStatus>('loading')
  const [siteSections, setSiteSections] = useState<CfoSiteProjectSection[]>([])
  const [adminToolsPath, setAdminToolsPath] = useState<string | null>(null)
  const [founderActivityAllowed, setFounderActivityAllowed] = useState(false)
  const [platformRole, setPlatformRole] = useState<'founder' | 'admin' | 'sales' | null>(null)
  const [tenantAdminSiteIds, setTenantAdminSiteIds] = useState<Set<string>>(() => new Set())
  const [actionError, setActionError] = useState<string | null>(null)
  const [connectSites, setConnectSites] = useState<ServerSite[]>([])
  const [siteSummaries, setSiteSummaries] = useState<ServerSiteSummary[]>([])
  const [moveWorkbookState, setMoveWorkbookState] = useState<MoveWorkbookState | null>(null)

  const [showConnect, setShowConnect] = useState(false)
  const [companyName, setCompanyName] = useState('')
  const [connectTargetMode, setConnectTargetMode] = useState<ConnectTargetMode>('new_site')
  const [connectSiteId, setConnectSiteId] = useState('')
  const [connectPhase, setConnectPhase] = useState<ConnectPhase>('idle')
  const [connectError, setConnectError] = useState<string | null>(null)
  const [connectProvider, setConnectProvider] = useState<AccountingProvider>('quickbooks')
  const [xeroEnabled, setXeroEnabled] = useState(false)
  const [providerCatalog, setProviderCatalog] = useState<AccountingProviderDescriptor[]>([])
  const [xeroGrantId, setXeroGrantId] = useState<string | null>(null)
  const [xeroOrganizations, setXeroOrganizations] = useState<XeroOrganizationCandidate[]>([])
  const [selectedXeroOrganizations, setSelectedXeroOrganizations] = useState<Set<string>>(() => new Set())
  const [sageCredentialId, setSageCredentialId] = useState<string | null>(null)
  const [sageBusinesses, setSageBusinesses] = useState<SageBusinessCandidate[]>([])
  const [selectedSageBusiness, setSelectedSageBusiness] = useState<string>('')
  const [quickBooksAuthorizer, setQuickBooksAuthorizer] = useState<QuickBooksAuthorizer>('me')
  // The CFO Core the connect dialog just made for a QuickBooks admin to
  // connect. While set, the dialog shows its link instead of the form.
  const [createdForAdmin, setCreatedForAdmin] = useState<(LinkTarget & { siteName: string }) | null>(null)
  // A row's (or its Manage menu's) link dialog.
  const [linkTarget, setLinkTarget] = useState<LinkTarget | null>(null)

  /**
   * Whether a provider can be connected right now.
   *
   * The backend catalog folds global status and the tenant rollout record into
   * `connector_enabled`, so this reads that rather than re-deriving
   * availability. A hard-coded provider list here would let the UI disagree
   * with what the API will actually accept, and would keep a provider dark
   * after its rollout was enabled server-side.
   */
  const providerAvailability = useCallback((provider: AccountingProvider) => {
    const entry = providerCatalog.find((item) => item.provider === provider)
    if (!entry) {
      // The catalog has not loaded, or the server does not know this provider.
      // Withhold rather than guess: a wrong "available" sends the customer
      // into an authorization the server will refuse.
      return { enabled: false, status: 'planned' as string }
    }
    // Xero keeps its dedicated availability endpoint as an additional signal.
    // The catalog only reports a tenant-scoped Xero as connectable once that
    // tenant has a rollout record, so reading the catalog alone would switch
    // off a Xero private beta that is live today. Xero behaviour is meant to
    // be untouched by the Sage rollout, so its existing source still counts.
    const enabled = entry.connector_enabled || (provider === 'xero' && xeroEnabled)
    return { enabled, status: entry.status }
  }, [providerCatalog, xeroEnabled])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const canonical = cfoSelectorCanonicalPath(window.location.pathname, window.location.search)
    if (!canonical) return
    window.history.replaceState({}, '', canonical)
  }, [])

  const load = useCallback(async () => {
    setStatus('loading')
    try {
      const session = await ensureApiSessionReady()
      if (!session.token) {
        setSiteSections([])
        setSiteSummaries([])
        setAdminToolsPath(null)
        setFounderActivityAllowed(false)
        setPlatformRole(null)
        setTenantAdminSiteIds(new Set())
        setConnectSites([])
        setStatus('error')
        return
      }
      const [mySites, tenantAdminSitesResult, founderActivityAccess, platformAccess, xeroAvailability, accountingProviders] = await Promise.all([
        api.listMySites(),
        api.listAdminSites()
          .then((value) => ({ ok: true as const, value }))
          .catch(() => ({ ok: false as const, value: null })),
        api.getFounderActivityAccess()
          .then((value) => ({ ok: true as const, value }))
          .catch(() => ({ ok: false as const, value: null })),
        api.getPlatformAccess().catch(() => ({ role: null })),
        api.getXeroAvailability().catch(() => ({ connector_enabled: false, self_serve_enabled: false, gl_enabled: false })),
        api.getAccountingProviders().catch(() => ({ providers: [] })),
      ])
      setXeroEnabled(xeroAvailability.connector_enabled === true)
      setProviderCatalog(accountingProviders.providers)
      const editableSites = (Array.isArray(mySites) ? mySites : []).filter(cfoSiteCanAddExperience)
      setConnectSites(editableSites)
      const summaries = await Promise.all(
        (Array.isArray(mySites) ? mySites : [])
          .filter((site) => !!site.slug)
          .map((site) => api.getSiteSummary(site.slug).then((value) => ({ ok: true as const, value })).catch(() => ({ ok: false as const, value: null }))),
      )
      const loadedSummaries: ServerSiteSummary[] = summaries
        .map((result) => (result.ok ? result.value : null))
        .filter((summary): summary is ServerSiteSummary => !!summary)
      setSiteSummaries(loadedSummaries)
      setSiteSections(selectCfoSiteProjectSections(loadedSummaries))
      setTenantAdminSiteIds(new Set(
        tenantAdminSitesResult.ok && Array.isArray(tenantAdminSitesResult.value)
          ? tenantAdminSitesResult.value.map((site) => site.id)
          : [],
      ))
      setAdminToolsPath(resolveCfoAdminToolsPath(
        tenantAdminSitesResult.ok ? tenantAdminSitesResult.value : null,
        Array.isArray(mySites) ? mySites : null,
      ))
      setFounderActivityAllowed(founderActivityAccess.ok && founderActivityAccess.value?.allowed === true)
      setPlatformRole(platformAccess.role)
      setStatus('ready')
    } catch {
      setSiteSections([])
      setSiteSummaries([])
      setAdminToolsPath(null)
      setFounderActivityAllowed(false)
      setPlatformRole(null)
      setTenantAdminSiteIds(new Set())
      setConnectSites([])
      setStatus('error')
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!showConnect) return
    let cancelled = false
    // Fetch with or without a site. Provider availability now comes only from
    // this catalog, so skipping the call when no site is chosen yet — the
    // "new site" path — would leave every provider disabled, including
    // QuickBooks. Without a site the server returns global status; with one it
    // folds in that tenant's rollout.
    void api.getAccountingProviders(connectSiteId || null)
      .then((catalog) => {
        if (!cancelled) setProviderCatalog(catalog.providers)
      })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [api, connectSiteId, showConnect])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('xero_status') !== 'authorized') return
    setConnectProvider('xero')
    setShowConnect(true)
    setConnectPhase('creating')
    void ensureApiSessionReady()
      .then(() => api.exchangeXeroAccountingHandoff())
      .then(({ grant_id: grantId }) => {
        setXeroGrantId(grantId)
        return api.listXeroOrganizations(grantId)
      })
      .then((organizations) => {
      setXeroOrganizations(organizations)
      setSelectedXeroOrganizations(new Set(organizations.map((org) => org.organization_id)))
      setConnectPhase('idle')
      window.history.replaceState({}, '', window.location.pathname)
    }).catch((err: any) => {
      setConnectPhase('idle')
      setConnectError(String(err?.message || err || 'Could not load the Xero organizations.'))
    })
  }, [api])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const credentialId = params.get('sage_accounting_credential_id')
    if (!credentialId || params.get('sage_accounting_status') !== 'authorized') return
    setConnectProvider('sage_accounting')
    setSageCredentialId(credentialId)
    setShowConnect(true)
    setConnectPhase('creating')
    void ensureApiSessionReady()
      .then(() => api.listSageAccountingBusinesses(credentialId))
      .then((businesses) => {
        setSageBusinesses(businesses)
        // Pre-select only when the choice is unambiguous. With several
        // businesses the customer picks, so a workbook is never bound to the
        // wrong company by default.
        setSelectedSageBusiness(businesses.length === 1 ? businesses[0].provider_account_id : '')
        setConnectPhase('idle')
        window.history.replaceState({}, '', window.location.pathname)
      })
      .catch((err: any) => {
        setConnectPhase('idle')
        setConnectError(String(err?.message || err || 'Could not load the Sage businesses.'))
      })
  }, [api])

  const openExperience = useCallback((siteSlug: string, wb: ServerSiteWorkbookSummary) => {
    navigateTo(buildCfoRoutePath({ siteSlug, workbookId: wb.id, page: 'home' }))
  }, [])

  const renameSite = useCallback((site: ServerSite) => {
    const name = window.prompt('Rename site', site.name)
    if (!name?.trim() || name.trim() === site.name) return
    setActionError(null)
    void api.updateAdminSite(site.id, { name: name.trim() })
      .then(() => load())
      .catch((err: any) => setActionError(String(err?.message || err || 'Failed to rename site.')))
  }, [api, load])

  const renameProject = useCallback((siteSlug: string, project: ServerProject) => {
    const name = window.prompt('Rename project', project.name)
    if (!name?.trim() || name.trim() === project.name) return
    setActionError(null)
    void api.updateSiteProject(siteSlug, project.id, { name: name.trim() })
      .then(() => load())
      .catch((err: any) => setActionError(String(err?.message || err || 'Failed to rename project.')))
  }, [api, load])

  const openMoveWorkbook = useCallback((section: CfoSiteProjectSection, workbook: ServerSiteWorkbookSummary) => {
    setMoveWorkbookState({
      workbook,
      sourceSite: section.site,
      targetSiteId: section.site.id,
      targetProjectId: workbook.project_id,
      saving: false,
      error: null,
    })
  }, [])

  const submitMoveWorkbook = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!moveWorkbookState) return
    const siteId = moveWorkbookState.targetSiteId.trim()
    const projectId = moveWorkbookState.targetProjectId.trim()
    if (!siteId && !projectId) {
      setMoveWorkbookState((prev) => prev ? { ...prev, error: 'Choose a destination site or project.' } : prev)
      return
    }
    setMoveWorkbookState((prev) => prev ? { ...prev, saving: true, error: null } : prev)
    try {
      await api.moveWorkbook(moveWorkbookState.workbook.id, {
        site_id: siteId || null,
        project_id: projectId || null,
      })
      setMoveWorkbookState(null)
      await load()
    } catch (err: any) {
      setMoveWorkbookState((prev) => prev ? {
        ...prev,
        saving: false,
        error: String(err?.message || err || 'Failed to move workbook.'),
      } : prev)
    }
  }, [api, load, moveWorkbookState])

  const openConnect = useCallback(() => {
    setConnectError(null)
    setCompanyName('')
    setConnectTargetMode(connectSites.length ? 'existing_site' : 'new_site')
    setConnectSiteId(connectSites[0]?.id || '')
    setConnectProvider('quickbooks')
    setXeroGrantId(null)
    setXeroOrganizations([])
    setSelectedXeroOrganizations(new Set())
    setSageCredentialId(null)
    setSageBusinesses([])
    setSelectedSageBusiness('')
    setQuickBooksAuthorizer('me')
    setCreatedForAdmin(null)
    setShowConnect(true)
  }, [connectSites])

  const closeConnect = useCallback(() => {
    setShowConnect(false)
    if (createdForAdmin) {
      setCreatedForAdmin(null)
      void load()
    }
  }, [createdForAdmin, load])

  const submitConnect = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const company = companyName.trim()
      if (connectProvider === 'quickbooks' && !company) {
        setConnectError('Please enter the company name.')
        return
      }
      const siteId = connectTargetMode === 'existing_site' ? connectSiteId.trim() : ''
      if (connectTargetMode === 'existing_site' && !siteId) {
        setConnectError('Choose the site for this accounting company.')
        return
      }
      setConnectError(null)
      setConnectPhase('creating')
      try {
        if (!providerAvailability(connectProvider).enabled) {
          throw new Error(`${PROVIDER_NAMES[connectProvider]} is not accepting live connections yet.`)
        }
        if (connectProvider === 'sage_accounting') {
          if (!sageCredentialId) {
            // Sage returns to this page with a credential id, which the second
            // pass below exchanges for the business list.
            const redirect = `${window.location.origin}${window.location.pathname}`
            const out = await api.createSageAccountingAuthorizeUrl({
              redirect,
              site_id: siteId || null,
            })
            if (!out?.authorize_url) throw new Error('Could not start the Sage connection.')
            setConnectPhase('connecting')
            window.location.href = out.authorize_url
            return
          }
          if (!siteId) throw new Error('Choose the site for this Sage business.')
          if (!selectedSageBusiness) throw new Error('Choose the Sage business to connect.')
          // Discover the entity and primary book before provisioning: Sage
          // carries the base currency and fiscal year end on financial
          // settings, not on the business, and the workbook needs both.
          const entities = await api.listSageAccountingEntities(sageCredentialId, selectedSageBusiness)
          const entity = entities[0]
          if (!entity) throw new Error('Sage returned no entity for this business.')
          const books = await api.listSageAccountingBooks(
            sageCredentialId, selectedSageBusiness, entity.provider_entity_id,
          )
          const book = books.find((candidate) => candidate.is_primary) || books[0]
          if (!book) throw new Error('Sage returned no accounting book for this business.')
          // Surface a permission gap here rather than provisioning a company
          // that would sit degraded after its first sync. Missing Journals
          // access means no GL detail, never an empty ledger.
          const capabilities = await api.probeSageAccountingCapabilities({
            credential_id: sageCredentialId,
            business_id: selectedSageBusiness,
            entity_id: entity.provider_entity_id,
            book_id: book.provider_book_id,
          })
          const denied = Object.entries(capabilities.dataset_permissions)
            .filter(([, granted]) => !granted)
            .map(([dataset]) => dataset)
          if (denied.length) {
            throw new Error(
              `This Sage user cannot read: ${denied.join(', ')}. Grant access in Sage and reconnect.`,
            )
          }
          await api.provisionSageAccountingCompany({
            credential_id: sageCredentialId,
            site_id: siteId,
            business_id: selectedSageBusiness,
            entity_id: entity.provider_entity_id,
            book_id: book.provider_book_id,
            idempotency_key: `sage-ui-${sageCredentialId}-${selectedSageBusiness}`,
          })
          window.location.assign('/onboarding/sync-started?provider=sage_accounting')
          return
        }
        if (connectProvider === 'xero') {
          if (!xeroGrantId) {
            const redirect = `${window.location.origin}${window.location.pathname}`
            const out = await api.createXeroAuthorizeUrl(redirect)
            if (!out?.authorize_url) throw new Error('Could not start the Xero connection.')
            setConnectPhase('connecting')
            window.location.href = out.authorize_url
            return
          }
          const organizationIds = Array.from(selectedXeroOrganizations)
          if (!organizationIds.length) throw new Error('Choose at least one Xero organization.')
          const result = await api.provisionXeroOrganizations({
            grant_id: xeroGrantId,
            organization_ids: organizationIds,
            site_id: siteId || null,
            idempotency_key: `xero-ui-${xeroGrantId}-${organizationIds.slice().sort().join('-')}`,
          })
          const first = result.companies[0]
          if (!first) throw new Error('No Xero company was provisioned.')
          window.location.assign('/onboarding/sync-started?provider=xero')
          return
        }
        const { site, workbook } = await api.createCfoExperience({
          company_name: company,
          site_id: siteId || null,
        })
        if (quickBooksAuthorizer === 'admin') {
          // The CFO Core exists now, so the dialog swaps to its link. Making
          // the link is the panel's job: if that fails, retrying there reuses
          // this CFO Core instead of creating another site.
          setCreatedForAdmin({
            workbookId: workbook.id,
            companyLabel: company,
            reconnect: false,
            siteName: site?.name || company,
            siteSlug: site?.slug || null,
          })
          setConnectPhase('idle')
          return
        }
        // Hand off to QuickBooks. The server callback binds the connection to this
        // workbook, runs the first sync, and (because of the cfo_add_company intent)
        // auto-provisions the CFO layout regardless of signup_source.
        const redirect = `${window.location.origin}/onboarding/sync-started?provider=quickbooks`
        const out = await api.createQuickBooksAuthorizeUrl({
          intent: 'cfo_add_company',
          workbook_id: workbook.id,
          redirect,
        })
        if (!out?.authorize_url) throw new Error('Could not start the QuickBooks connection.')
        setConnectPhase('connecting')
        window.location.href = out.authorize_url
      } catch (err: any) {
        setConnectPhase('idle')
        setConnectError(String(err?.message || err || 'Something went wrong. Please try again.'))
      }
    },
    [api, companyName, connectProvider, connectSiteId, connectTargetMode, providerAvailability, quickBooksAuthorizer, sageCredentialId, selectedSageBusiness, selectedXeroOrganizations, xeroGrantId],
  )

  const signOut = useCallback(async () => {
    try {
      await api.logout()
    } catch {
      // best-effort
    }
    api.setToken(null)
    window.location.assign('/')
  }, [api])

  const renderWorkbook = (
    section: CfoSiteProjectSection,
    workbook: ServerSiteWorkbookSummary,
  ) => {
    const canMove = workbook.can_edit && (tenantAdminSiteIds.has(section.site.id) || cfoSiteCanManageProjects(section.site))
    const providerKey = PROVIDER_ORDER.includes(workbook.accounting_provider as AccountingProvider)
      ? workbook.accounting_provider as AccountingProvider
      : 'quickbooks'
    const provider = PROVIDER_NAMES[providerKey]
    const connectionStatus = workbook.accounting_connection_status || workbook.qbo_connection_status
    const disconnected = connectionStatus === 'needs_reconnect' || connectionStatus === 'revoked'
    const companyLabel = workbook.qbo_company_name?.trim() || companyLabelFromWorkbookName(workbook.name)
    // A CFO Core made for its QuickBooks admin to connect, who has not yet.
    // It has no books to open, so the row opens its link instead.
    const linkStatus = workbook.qbo_connect_link_status
    const awaitingAdmin = providerKey === 'quickbooks' && cfoWorkbookAwaitingQuickBooksAdmin(workbook)
    const canShareLink = workbook.can_edit && providerKey === 'quickbooks'
    const openLink = (reconnect: boolean) => setLinkTarget({
      workbookId: workbook.id,
      companyLabel,
      reconnect,
      siteSlug: section.site.slug || null,
    })
    const activate = () => {
      if (awaitingAdmin) {
        if (canShareLink) openLink(false)
        return
      }
      openExperience(section.site.slug, workbook)
    }
    return (
      <div
        key={workbook.id}
        className={`cfo-xp-wb${disconnected ? ' is-disconnected' : ''}${awaitingAdmin ? ' is-awaiting' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={awaitingAdmin
          ? `${workbook.name}: waiting for the QuickBooks admin to connect${canShareLink ? '. Open its QuickBooks link' : ''}`
          : `Open ${workbook.name} CFO Core${disconnected ? ` (${provider} disconnected)` : ''}`}
        onClick={activate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            activate()
          }
        }}
      >
        <span className="cfo-xp-wb-icon" aria-hidden="true">
          <WorkbookGlyph />
        </span>
        <div className="cfo-xp-wb-text">
          <div className="cfo-xp-wb-name">{workbook.name}</div>
          <div className="cfo-xp-wb-tag">CFO Core · {provider}</div>
          {disconnected ? (
            <div className="cfo-xp-wb-disconnected" role="status">
              {provider} disconnected — reports are frozen until
              {' '}{workbook.accounting_company_name?.trim() || workbook.qbo_company_name?.trim() || 'the client'} reconnects
            </div>
          ) : null}
          {awaitingAdmin ? (
            <div className={`cfo-xp-wb-awaiting${linkStatus === 'revoked' ? ' is-revoked' : ''}`} role="status">
              {linkStatus === 'revoked'
                ? 'Link revoked — QuickBooks is not connected yet'
                : 'Waiting for the QuickBooks admin to connect'}
            </div>
          ) : null}
        </div>
        <div className="cfo-xp-wb-actions">
          {canShareLink && (awaitingAdmin || disconnected) ? (
            <button
              type="button"
              className="btn cfo-xp-wb-link"
              onClick={(e) => {
                e.stopPropagation()
                openLink(disconnected)
              }}
              onKeyDown={(e) => e.stopPropagation()}
              title={disconnected
                ? `Get a link the ${companyLabel} QuickBooks admin can use to reconnect`
                : `The link for the ${companyLabel} QuickBooks admin`}
            >
              {disconnected ? 'Reconnect link' : linkStatus === 'revoked' ? 'New link' : 'Share link'}
            </button>
          ) : null}
          {canMove ? (
            <button
              type="button"
              className="btn ghost cfo-xp-wb-move"
              onClick={(e) => {
                e.stopPropagation()
                openMoveWorkbook(section, workbook)
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              Move
            </button>
          ) : null}
          <div
            className="cfo-xp-wb-manage"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <CfoCoreFileMenu
              appApi={api}
              workbookId={workbook.id}
              accountingProvider={workbook.accounting_provider}
              triggerLabel="Manage"
              onDeleted={load}
              onRenamed={load}
              onConnectionChanged={load}
              onShareConnectLink={(target) => setLinkTarget({ ...target, companyLabel, siteSlug: section.site.slug || null })}
            />
          </div>
          {awaitingAdmin ? null : (
            <span className="cfo-xp-open">
              Open <span className="cfo-xp-open-arrow" aria-hidden="true">&rarr;</span>
            </span>
          )}
        </div>
      </div>
    )
  }

  const renderProjectNode = (
    section: CfoSiteProjectSection,
    node: CfoProjectNode,
    depth = 0,
  ): React.ReactNode => (
    <div key={node.project.id} className={`cfo-xp-project${depth === 0 ? ' root' : ''}`}>
      <div className="cfo-xp-project-head">
        <span className="cfo-xp-project-icon" aria-hidden="true">
          <ProjectGlyph />
        </span>
        <div className="cfo-xp-project-head-text">
          <span className="cfo-xp-project-eyebrow">Project</span>
          <div className="cfo-xp-project-name">{node.project.name}</div>
        </div>
        <span className="cfo-xp-project-meta">
          {node.workbooks.length} {node.workbooks.length === 1 ? 'experience' : 'experiences'}
          {node.children.length ? ` · ${node.children.length} nested` : ''}
        </span>
        {cfoSiteCanManageProjects(section.site) ? (
          <button type="button" className="btn ghost cfo-xp-head-action" onClick={() => renameProject(section.site.slug, node.project)}>
            Rename
          </button>
        ) : null}
      </div>
      {node.workbooks.length ? (
        <div className="cfo-xp-books cfo-xp-project-books">
          {node.workbooks.map((workbook) => renderWorkbook(section, workbook))}
        </div>
      ) : null}
      {node.children.map((child) => renderProjectNode(section, child, depth + 1))}
    </div>
  )

  // Full-screen handoff while we redirect to Intuit.
  if (connectPhase === 'connecting') {
    return (
      <div className="cfo-shell" style={shellStyle}>
        <div className="cfo-shell-content" style={{ display: 'grid', placeItems: 'center', minHeight: '70vh' }}>
          <div className="card lead" style={{ maxWidth: 440, textAlign: 'center' }}>
            <div className="body" style={{ display: 'block' }}>
              <div className="cfo-shell-loading-orb" aria-hidden="true" style={{ margin: '0 auto var(--s4)' }} />
              <h3>Connecting to {PROVIDER_NAMES[connectProvider]}…</h3>
              <p>Taking you to {connectProvider === 'quickbooks' ? 'Intuit' : PROVIDER_NAMES[connectProvider]} to authorize read-only access. Hang tight.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const selectedMoveSiteSummary = moveWorkbookState
    ? siteSummaries.find((summary) => summary.site.id === moveWorkbookState.targetSiteId) || null
    : null
  const moveTargetProjects = selectedMoveSiteSummary?.projects || []
  const awaitingAdminCount = siteSections
    .flatMap((section) => cfoSiteProjectWorkbooks(section))
    .filter(cfoWorkbookAwaitingQuickBooksAdmin)
    .length
  const partnerBillingWorkbookId = siteSections
    .filter((section) => tenantAdminSiteIds.has(section.site.id))
    .flatMap((section) => cfoSiteProjectWorkbooks(section))
    .map((workbook) => workbook.id)
    .find(Boolean) || ''

  return (
    <div className="cfo-shell" style={shellStyle}>
      <div className="cfo-shell-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--s3)', marginBottom: 'var(--s7)' }}>
          <CfoBrandMark sub="by FuseGrid" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {platformRole ? (
              <button type="button" className="btn ghost" onClick={() => navigateTo('/admin/tenants')}>
                All tenants
              </button>
            ) : null}
            {founderActivityAllowed ? (
              <button type="button" className="btn ghost cfo-xp-founder-link" onClick={() => navigateTo('/founder/activity')}>
                Founder activity
              </button>
            ) : null}
            {adminToolsPath ? (
              <button type="button" className="btn ghost" onClick={() => navigateTo(adminToolsPath)}>
                Admin tools
              </button>
            ) : null}
            {partnerBillingWorkbookId ? (
              <button type="button" className="btn ghost" onClick={() => navigateTo(`/billing/partner?workbook_id=${encodeURIComponent(partnerBillingWorkbookId)}`)}>
                Partner billing
              </button>
            ) : null}
            <button type="button" className="btn ghost" onClick={() => navigateTo('/connect')}>
              AI connectors
            </button>
            <button type="button" className="btn ghost" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>

        <div className="pagehead">
          <div className="eyebrow">Your CFO Core</div>
          <h1>CFO experiences</h1>
          <p className="lede">
            Open a company&rsquo;s live CFO Core, or <em>connect another accounting company</em> to
            start a new one.
          </p>
        </div>

        <div style={{ marginBottom: 'var(--s7)' }}>
          <button
            type="button"
            className="btn primary"
            onClick={openConnect}
            style={{ padding: '10px 18px', fontSize: 'var(--fs-md)' }}
          >
            + Connect a new accounting company
          </button>
        </div>

        {actionError ? (
          <div className="card bad edge" style={{ marginBottom: 'var(--s5)' }}>
            <div className="body">
              <div className="grow">
                <h3>Update failed</h3>
                <p>{actionError}</p>
              </div>
            </div>
          </div>
        ) : null}

        {status === 'loading' ? (
          <>
            <div className="cfo-shell-loading-note">
              <span className="cfo-shell-loading-orb" aria-hidden="true" />
              Loading your companies&hellip;
            </div>
            <div className="cards">
              {[0, 1].map((i) => (
                <div key={i} className="card">
                  <div className="body">
                    <div className="grow">
                      <span className="cfo-shell-skel cfo-shell-skel-line wide" />
                      <span className="cfo-shell-skel cfo-shell-skel-line" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : status === 'error' ? (
          <div className="card bad edge">
            <div className="body">
              <div className="grow">
                <h3>We couldn&rsquo;t load your companies</h3>
                <p>Something went wrong reaching the server.</p>
              </div>
              <div className="figc">
                <button type="button" className="btn" onClick={() => void load()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        ) : siteSections.length === 0 ? (
          <div className="card lead edge">
            <div className="body">
              <div className="grow">
                <h3>No CFO Core yet</h3>
                <p>Connect an available accounting platform to build your first CFO Core experience.</p>
              </div>
              <div className="figc">
                <button type="button" className="btn primary" onClick={openConnect}>
                  Connect accounting company
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="section-title">
              <h2>Sites</h2>
              <span className="meta">
                {siteSections.length} {siteSections.length === 1 ? 'site' : 'sites'} ·{' '}
                {siteSections.reduce((total, section) => total + section.workbookCount, 0) - awaitingAdminCount} connected
                {awaitingAdminCount ? ` · ${awaitingAdminCount} waiting for QuickBooks` : ''}
              </span>
            </div>
            <div className="cfo-xp-list">
              {siteSections.map((section) => (
                <article key={section.site.id} className="cfo-xp-site">
                  <header className="cfo-xp-site-head">
                    <span className="cfo-xp-site-icon" aria-hidden="true">
                      <SiteGlyph />
                    </span>
                    <div className="cfo-xp-site-head-text">
                      <span className="cfo-xp-eyebrow">Site</span>
                      <div className="cfo-xp-site-name">{section.site.name}</div>
                      <div className="cfo-xp-site-count">
                        {section.workbookCount} {section.workbookCount === 1 ? 'CFO Core experience' : 'CFO Core experiences'}
                      </div>
                    </div>
                    <div className="cfo-xp-site-actions">
                      {section.workbookCount >= 2 ? (
                        <button
                          type="button"
                          className="btn cfo-xp-head-action"
                          onClick={() => navigateTo(buildFirmCockpitPath(section.site.slug))}
                          title="Manage this firm's whole book of clients"
                        >
                          Firm cockpit →
                        </button>
                      ) : null}
                      {cfoSiteCanManageUsers(section.site) ? (
                        <button
                          type="button"
                          className="btn ghost cfo-xp-head-action"
                          onClick={() => navigateTo(`/${encodeURIComponent(section.site.slug)}/users`)}
                        >
                          Manage users
                        </button>
                      ) : null}
                      {tenantAdminSiteIds.has(section.site.id) ? (
                        <button type="button" className="btn ghost cfo-xp-head-action" onClick={() => renameSite(section.site)}>
                          Rename site
                        </button>
                      ) : null}
                    </div>
                  </header>
                  <div className="cfo-xp-site-body">
                    {cfoSiteUsesProjects(section) ? (
                      <>
                        {section.projects.map((project) => renderProjectNode(section, project))}
                        {section.unassignedWorkbooks.length ? (
                          <div className="cfo-xp-project unassigned root">
                            <div className="cfo-xp-project-head">
                              <span className="cfo-xp-project-icon" aria-hidden="true">
                                <ProjectGlyph />
                              </span>
                              <div className="cfo-xp-project-head-text">
                                <span className="cfo-xp-project-eyebrow">Project</span>
                                <div className="cfo-xp-project-name">No project</div>
                              </div>
                              <span className="cfo-xp-project-meta">
                                {section.unassignedWorkbooks.length} {section.unassignedWorkbooks.length === 1 ? 'experience' : 'experiences'}
                              </span>
                            </div>
                            <div className="cfo-xp-books cfo-xp-project-books">
                              {section.unassignedWorkbooks.map((workbook) => renderWorkbook(section, workbook))}
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="cfo-xp-books">
                        {cfoSiteProjectWorkbooks(section).map((workbook) => renderWorkbook(section, workbook))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>

      {showConnect ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Connect a new accounting company"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(28, 31, 38, 0.42)',
            display: 'grid',
            placeItems: 'center',
            padding: 'var(--s5)',
            zIndex: 1000,
          }}
          onClick={() => connectPhase === 'idle' && closeConnect()}
        >
          {createdForAdmin ? (
            <div
              className="card lead"
              style={{ width: 'min(520px, 100%)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="body" style={{ display: 'block' }}>
                <h3 style={{ fontSize: 'var(--fs-lg)' }}>Send this link to the QuickBooks admin</h3>
                <p style={{ marginBottom: 'var(--s4)' }}>
                  <strong>{createdForAdmin.companyLabel} CFO Core</strong> is set up in {createdForAdmin.siteName}.
                  Choose who the request comes from (for example the company&rsquo;s CFO), then send the link to
                  whoever administers {createdForAdmin.companyLabel} in QuickBooks. When they connect, the first
                  sync starts on its own.
                </p>
                <QuickBooksConnectLinkPanel
                  api={api}
                  workbookId={createdForAdmin.workbookId}
                  siteSlug={createdForAdmin.siteSlug}
                  companyLabel={createdForAdmin.companyLabel}
                  autoCreate
                />
                <p className="cfo-provider-note" style={{ textAlign: 'left', marginTop: 'var(--s4)' }}>
                  The CFO Core shows as waiting until the admin connects. You can get this link again from its row.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--s4)' }}>
                  <button type="button" className="btn primary" onClick={closeConnect}>
                    Done
                  </button>
                </div>
              </div>
            </div>
          ) : (
          <form
            className="card lead"
            style={{ width: 'min(460px, 100%)' }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitConnect}
          >
            <div className="body" style={{ display: 'block' }}>
              <h3 style={{ fontSize: 'var(--fs-lg)' }}>Connect a new accounting company</h3>
              <p style={{ marginBottom: 'var(--s4)' }}>
                Choose the accounting provider. We&rsquo;ll create one CFO Core per selected company
                after you authorize read-only access.
              </p>
              <div className="cfo-xp-provider-grid">
                {PROVIDER_ORDER.map((provider) => {
                  const { enabled, status } = providerAvailability(provider)
                  const statusLabel = status === 'planned' ? 'Planned' : status === 'disabled' ? 'Unavailable' : 'Full suite'
                  return <button
                    key={provider}
                    type="button"
                    className={connectProvider === provider ? 'btn primary cfo-xp-provider-btn' : 'btn ghost cfo-xp-provider-btn'}
                    onClick={() => enabled && setConnectProvider(provider)}
                    disabled={connectPhase !== 'idle' || !!xeroGrantId || !!sageCredentialId || !enabled}
                    title={!enabled ? `${PROVIDER_NAMES[provider]} is ${statusLabel.toLowerCase()}` : undefined}
                  >
                    <AccountingProviderMark provider={provider} />
                    <span>{PROVIDER_NAMES[provider]}<small>{statusLabel}</small></span>
                  </button>
                })}
              </div>
              {connectProvider === 'xero' && xeroEnabled ? (
                <p className="cfo-provider-note" style={{ marginBottom: 'var(--s4)' }}>
                  Xero runs the complete CFO suite: statements, ratios, A/R, 13-week cash, forecasts, close,
                  and reporting — plus transaction-level ledger detail rebuilt from your invoices, bills,
                  payments, and bank transactions, cross-checked to the penny against Xero&rsquo;s own reports
                  every month.
                </p>
              ) : null}
              {connectError ? (
                <p style={{ color: 'var(--neg-ink)', marginBottom: 'var(--s3)', fontWeight: 600 }}>
                  {connectError}
                </p>
              ) : null}
              {connectSites.length ? (
                <div style={{ display: 'grid', gap: 'var(--s3)', marginBottom: 'var(--s4)' }}>
                  <div style={{ display: 'flex', gap: 'var(--s2)', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className={connectTargetMode === 'existing_site' ? 'btn primary' : 'btn ghost'}
                      onClick={() => {
                        setConnectTargetMode('existing_site')
                        setConnectSiteId((current) => current || connectSites[0]?.id || '')
                      }}
                      disabled={connectPhase !== 'idle'}
                    >
                      Existing site
                    </button>
                    <button
                      type="button"
                      className={connectTargetMode === 'new_site' ? 'btn primary' : 'btn ghost'}
                      onClick={() => setConnectTargetMode('new_site')}
                      disabled={connectPhase !== 'idle'}
                    >
                      New site
                    </button>
                  </div>
                  {connectTargetMode === 'existing_site' ? (
                    <label style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                      Add to site
                      <select
                        value={connectSiteId}
                        onChange={(e) => setConnectSiteId(e.target.value)}
                        disabled={connectPhase !== 'idle'}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 'var(--r1)',
                          border: '1px solid var(--hair)',
                          background: 'var(--surface)',
                          color: 'var(--ink)',
                          fontSize: 'var(--fs-base)',
                        }}
                      >
                        {connectSites.map((site) => (
                          <option key={site.id} value={site.id}>{site.name}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
              ) : null}
              {connectProvider === 'xero' && xeroGrantId && xeroOrganizations.length ? (
                <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s5)', display: 'grid', gap: 'var(--s2)' }}>
                  <legend style={{ fontWeight: 700, marginBottom: 'var(--s2)' }}>Choose Xero organizations</legend>
                  {xeroOrganizations.map((org) => (
                    <label key={org.organization_id} style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center' }}>
                      <input type="checkbox" checked={selectedXeroOrganizations.has(org.organization_id)} onChange={(event) => setSelectedXeroOrganizations((current) => { const next = new Set(current); if (event.target.checked) next.add(org.organization_id); else next.delete(org.organization_id); return next })} />
                      <span>{org.name}{org.country_code ? ` · ${org.country_code}` : ''}</span>
                    </label>
                  ))}
                </fieldset>
              ) : null}
              {connectProvider === 'sage_accounting' && sageCredentialId && sageBusinesses.length ? (
                <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s5)', display: 'grid', gap: 'var(--s2)' }}>
                  <legend style={{ fontWeight: 700, marginBottom: 'var(--s2)' }}>Choose the Sage business</legend>
                  {sageBusinesses.map((business) => (
                    <label
                      key={business.provider_account_id}
                      style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center' }}
                    >
                      <input
                        type="radio"
                        name="sage-business"
                        checked={selectedSageBusiness === business.provider_account_id}
                        onChange={() => setSelectedSageBusiness(business.provider_account_id)}
                      />
                      <span>
                        {business.display_name}
                        {business.country_code ? ` · ${business.country_code}` : ''}
                      </span>
                    </label>
                  ))}
                  <p className="cfo-provider-note" style={{ marginTop: 'var(--s2)' }}>
                    One CFO Core is created per Sage business, using its primary accounting book.
                  </p>
                </fieldset>
              ) : null}
              {connectProvider === 'quickbooks' ? <><label
                htmlFor="cfo-connect-company"
                style={{
                  display: 'block',
                  fontSize: 'var(--fs-cap)',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                  marginBottom: 6,
                }}
              >
                {connectTargetMode === 'existing_site' ? 'QuickBooks company name' : 'New site / company name'}
              </label>
              <input
                id="cfo-connect-company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="organization"
                placeholder="Acme, Inc."
                autoFocus
                disabled={connectPhase !== 'idle'}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 'var(--r1)',
                  border: '1px solid var(--hair)',
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  fontSize: 'var(--fs-base)',
                  marginBottom: 'var(--s4)',
                }}
              />
              <fieldset className="cfo-xp-authorizer" disabled={connectPhase !== 'idle'}>
                <legend>Who will sign in to QuickBooks?</legend>
                <label className={quickBooksAuthorizer === 'me' ? 'is-selected' : undefined}>
                  <input
                    type="radio"
                    name="cfo-qbo-authorizer"
                    value="me"
                    checked={quickBooksAuthorizer === 'me'}
                    onChange={() => setQuickBooksAuthorizer('me')}
                  />
                  <span>
                    <strong>I will</strong>
                    <small>Continue to QuickBooks now and authorize the company yourself.</small>
                  </span>
                </label>
                <label className={quickBooksAuthorizer === 'admin' ? 'is-selected' : undefined}>
                  <input
                    type="radio"
                    name="cfo-qbo-authorizer"
                    value="admin"
                    checked={quickBooksAuthorizer === 'admin'}
                    onChange={() => setQuickBooksAuthorizer('admin')}
                  />
                  <span>
                    <strong>The company&rsquo;s QuickBooks admin</strong>
                    <small>
                      Get a link to send them. They don&rsquo;t need a FuseGrid login, and the link doesn&rsquo;t
                      expire.
                    </small>
                  </span>
                </label>
              </fieldset>
              </> : null}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--s3)' }}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={closeConnect}
                  disabled={connectPhase !== 'idle'}
                >
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={connectPhase !== 'idle'}>
                  {connectPhase === 'creating'
                    ? 'Setting up…'
                    : connectProvider === 'xero' && xeroGrantId
                      ? 'Add selected companies →'
                      : connectProvider === 'sage_accounting' && sageCredentialId
                        ? 'Add selected business →'
                        : connectProvider === 'quickbooks' && quickBooksAuthorizer === 'admin'
                          ? 'Create link →'
                          : `Continue to ${PROVIDER_NAMES[connectProvider]} →`}
                </button>
              </div>
            </div>
          </form>
          )}
        </div>
      ) : null}

      {linkTarget ? (
        <QuickBooksConnectLinkDialog
          api={api}
          workbookId={linkTarget.workbookId}
          siteSlug={linkTarget.siteSlug}
          companyLabel={linkTarget.companyLabel}
          reconnect={linkTarget.reconnect}
          onChanged={() => void load()}
          onClose={() => setLinkTarget(null)}
        />
      ) : null}

      {moveWorkbookState ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Move workbook"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(28, 31, 38, 0.42)',
            display: 'grid',
            placeItems: 'center',
            padding: 'var(--s5)',
            zIndex: 1000,
          }}
          onClick={() => !moveWorkbookState.saving && setMoveWorkbookState(null)}
        >
          <form
            className="card lead"
            style={{ width: 'min(520px, 100%)' }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitMoveWorkbook}
          >
            <div className="body" style={{ display: 'block' }}>
              <h3 style={{ fontSize: 'var(--fs-lg)' }}>Move workbook</h3>
              <p style={{ marginBottom: 'var(--s4)' }}>
                Move {moveWorkbookState.workbook.name} to another site or project.
              </p>
              {moveWorkbookState.error ? (
                <p style={{ color: 'var(--neg-ink)', marginBottom: 'var(--s3)', fontWeight: 600 }}>
                  {moveWorkbookState.error}
                </p>
              ) : null}
              <label style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-sm)', fontWeight: 700, marginBottom: 'var(--s4)' }}>
                Site
                <select
                  value={moveWorkbookState.targetSiteId}
                  onChange={(e) => {
                    const nextSiteId = e.target.value
                    setMoveWorkbookState((prev) => prev ? {
                      ...prev,
                      targetSiteId: nextSiteId,
                      targetProjectId: '',
                      error: null,
                    } : prev)
                  }}
                  disabled={moveWorkbookState.saving}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--r1)',
                    border: '1px solid var(--hair)',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    fontSize: 'var(--fs-base)',
                  }}
                >
                  {connectSites.map((site) => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-sm)', fontWeight: 700, marginBottom: 'var(--s5)' }}>
                Project
                <select
                  value={moveWorkbookState.targetProjectId}
                  onChange={(e) => setMoveWorkbookState((prev) => prev ? {
                    ...prev,
                    targetProjectId: e.target.value,
                    error: null,
                  } : prev)}
                  disabled={moveWorkbookState.saving}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 'var(--r1)',
                    border: '1px solid var(--hair)',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    fontSize: 'var(--fs-base)',
                  }}
                >
                  <option value="">Site default</option>
                  {moveTargetProjects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--s3)' }}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setMoveWorkbookState(null)}
                  disabled={moveWorkbookState.saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={moveWorkbookState.saving}>
                  {moveWorkbookState.saving ? 'Moving…' : 'Move workbook'}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}

export default CfoExperiencesPage
